// SalesReport.js
import React, { useState, useEffect } from 'react';
import { Tabs, Dropdown, Button, Menu, notification } from 'antd';
import {
  DownloadOutlined, FileTextOutlined, AppstoreOutlined,
  UserOutlined, ShoppingOutlined, FileExcelOutlined,
  FilePdfOutlined, PrinterOutlined, TableOutlined, ClockCircleOutlined
} from '@ant-design/icons';

import adminService from '../../../services/adminService';
import userService from '../../../services/userService';
import exportUtils from '../../../utils/exportUtils';
import userRoleUtils from '../../../utils/userRoleUtils';
import { itemReportQuantity } from '../../../utils/itemReportQuantity';

import ReportFilters from './ReportFilters';
import MetricsCards from './MetricsCards';
import OverviewTab from './OverviewTab';
import PropertySummaryTab from './PropertySummaryTab';
import OrderDetailsTab from './OrderDetailsTab';
import ItemReportTab from './ItemReportTab';
import HireDurationReportTab from './HireDurationReportTab';

import { pdf } from '@react-pdf/renderer';
import SalesReportPDF from './PDFDocument'; // Import the PDF component


// Import utilities and services
import { 
  processOrders, 
  calculateMetrics, 
  preparePropertySummaryData,
  getDefaultDateRange,
  formatDate,
  prepareOrderDetailsData
} from '../../../utils/dataUtils';
import jsPDF from 'jspdf';

const { TabPane } = Tabs;

const SalesReport = () => {
  // State for report data
  const [reportData, setReportData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [excludeCleaningServices, setExcludeCleaningServices] = useState(false);
  const [showPricesWithoutVAT, setShowPricesWithoutVAT] = useState(false);
  const VAT_RATE = 0.20; // 20% VAT

  // View mode for OrderDetailsTab
  const [orderDetailsViewMode, setOrderDetailsViewMode] = useState('default');

  // State for filters
  const [dateViewMode, setDateViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([]);
  const [dateDisplayText, setDateDisplayText] = useState('');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState(null);

  // Metrics state
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    successfulOrders: 0,
    failedOrders: 0,
    canceledOrders: 0, // Added for canceled orders
    averageOrderValue: 0
  });

  // Check if user is client/customer
  const [isClient, setIsClient] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [canViewAdminOnlyReports, setCanViewAdminOnlyReports] = useState(() => userRoleUtils.canViewSchedulingWarnings());

  // Initial fetch
  useEffect(() => {
    // Check if user is client/customer
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isUserClient = currentUser.role === 'customer' || (!currentUser.isAdmin && !currentUser.role?.includes('admin'));
    const userId = currentUser.id;
    const canViewReports = userRoleUtils.canViewSchedulingWarnings();

    setIsClient(isUserClient);
    setCurrentUserId(userId);
    setCanViewAdminOnlyReports(canViewReports);

    // If user is client, preselect their ID as filter
    if (isUserClient && userId) {
      setSelectedCustomerId(userId);
    }

    fetchCustomers();
    updateDateDisplayText();
  }, []);

  useEffect(() => {
    if (!canViewAdminOnlyReports && activeTab === 'items') {
      setActiveTab('overview');
    }
  }, [activeTab, canViewAdminOnlyReports]);

  // Update date display whenever date mode or current date changes
  useEffect(() => {
    updateDateDisplayText();
  }, [dateViewMode, currentDate]);

  // Fetch report data whenever filters change
  useEffect(() => {
    fetchReportData();
  }, [dateRange, selectedCustomerId, orderStatusFilter]);

  // Apply data processing whenever reportData or filtering options change
  useEffect(() => {
    if (reportData && reportData.length > 0) {
      // Process orders based on filters
      const processed = processOrders(reportData, {
        excludeCleaningServices,
        showPricesWithoutVAT
      });
      setProcessedData(processed);

      // Calculate metrics from processed data
      const calculatedMetrics = calculateMetrics(processed, {
        showPricesWithoutVAT,
        includeCanceled: true // Include canceled orders in metrics
      });
      setMetrics(calculatedMetrics);
    }
    else {
      setProcessedData([]);
      setMetrics({
        totalOrders: 0,
        totalRevenue: 0,
        successfulOrders: 0,
        failedOrders: 0,
        canceledOrders: 0, // Added for canceled orders
        averageOrderValue: 0
      });
    }
  }, [reportData, excludeCleaningServices, showPricesWithoutVAT]);

  const fetchCustomers = async () => {
    try {
      const params = { limit: 300 };
      const response = await userService.getAllCustomers(params);
      setCustomers(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching customers',
        description: error.message || 'Failed to load customer data'
      });
    }
  };

  function updateDateDisplayText() {
    const dayjs = require('dayjs');
    
    if (dateViewMode === 'day') {
      setDateDisplayText(dayjs(currentDate).format('DD MMM YYYY'));
      setDateRange(getDefaultDateRange('day', currentDate));
    } else if (dateViewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - day); // Set to previous Sunday

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6); // Set to Saturday

      setDateDisplayText(`${dayjs(startOfWeek).format('DD MMM')} - ${dayjs(endOfWeek).format('DD MMM YYYY')}`);
      setDateRange(getDefaultDateRange('week', currentDate));
    } else if (dateViewMode === 'month') {
      setDateDisplayText(dayjs(currentDate).format('MMMM YYYY'));
      setDateRange(getDefaultDateRange('month', currentDate));
    }
  }

  const handleDateNavigation = (direction) => {
    if (isCustomRange) return;

    const newDate = new Date(currentDate);

    if (dateViewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (dateViewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (dateViewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setCurrentDate(newDate);
    setDateRange(getDefaultDateRange(dateViewMode, newDate));
  };

  // Handler for updating Order details view mode
  const handleOrderDetailsViewMode = (viewMode) => {
    setOrderDetailsViewMode(viewMode);
  };

  const handleDateViewModeChange = (mode, newDate = null) => {
    setDateViewMode(mode);
    setIsCustomRange(false);
    
    // If a new date is provided, use it
    if (newDate) {
      setCurrentDate(newDate);
      setDateRange(getDefaultDateRange(mode, newDate));
    } else {
      // Otherwise use current date
      setDateRange(getDefaultDateRange(mode, currentDate));
    }
  };

  const handleCustomRangeChange = (dates) => {
    if (dates && (Array.isArray(dates) ? dates.length === 2 : true)) {
      const dayjs = require('dayjs');
      
      // Handle different formats of dates input
      let startDate, endDate;
      
      if (Array.isArray(dates)) {
        // From DatePicker.RangePicker
        const maxAllowedDate = dayjs().add(1, 'year');
        startDate = dates[0];
        endDate = dates[1];
        
        // Ensure dates are not too far in the future
        if (dayjs(endDate).isAfter(maxAllowedDate)) {
          endDate = maxAllowedDate.format('YYYY-MM-DD');
        }
      } else if (typeof dates === 'boolean' && !dates) {
        // Reset case
        setIsCustomRange(false);
        return;
      } else {
        // From DateRangeSelector - already formatted strings
        setDateRange(dates);
        setIsCustomRange(true);
        return;
      }
      
      // Format dates to YYYY-MM-DD
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      
      setDateRange([formattedStartDate, formattedEndDate]);
      setIsCustomRange(true);
    } else {
      setIsCustomRange(false);
    }
  };

  // Handle receiving data directly from DateRangeSelector
  const handleDataLoaded = (response) => {
    if (response && response.data) {
      // Process the returned data directly
      setReportData(response.data);
    }
  };

  const fetchReportData = async () => {
    setReportData([]);

    if (!dateRange || dateRange.length !== 2) return;

    setLoading(true);
    try {
      const params = {
        start_date: dateRange[0],
        end_date: dateRange[1]
      };

      // Add user filter if selected
      if (selectedCustomerId) {
        params.user_id = selectedCustomerId;
      }

      // Add status filter if selected
      if (orderStatusFilter === 'success') {
        params.status_type = 'success';
      } else if (orderStatusFilter === 'failed') {
        params.status_type = 'failed';
      } else if (orderStatusFilter === 'canceled') { // Add canceled filter
        params.status_type = 'canceled';
      }

      const response = await adminService.getSalesReport(
        params.start_date, 
        params.end_date, 
        params.user_id
      );

      // Process report data
      if (response && response.data) {
        let filteredOrders = response.data;

        // Apply search filter if provided
        if (searchText) {
          filteredOrders = filteredOrders.filter(order=>
            order.order_property?.property_title?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.delivery_first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.delivery_last_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.order_email?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.delivery_zip_code?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.order_property?.apartment_address?.toLowerCase().includes(searchText.toLowerCase())
          );
        }

        setReportData(filteredOrders);
      }
      else {
        setReportData([]);
      }
    } catch (error) {
      notification.error({
        message: 'Error fetching report data',
        description: error.message || 'Failed to load sales report data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (value) => {
    // If user is client, don't allow changing customer
    if (isClient) return;

    setSelectedCustomerId(value);
  };

  const handleStatusFilterChange = (value) => {
    setOrderStatusFilter(value);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    // Re-filter the existing data without making a new API call
    fetchReportData();
  };

  const handleResetFilters = () => {
    // Don't reset customer filter for client users
    if (!isClient) {
      setSelectedCustomerId(null);
    }

    setOrderStatusFilter(null);
    setSearchText('');
    setDateViewMode('month'); // Reset to default "this month"
    setCurrentDate(new Date());
    setIsCustomRange(false);
    setExcludeCleaningServices(false);
    setShowPricesWithoutVAT(false);
    setSelectedItemKeys([]);
    // This will trigger a data refresh through the useEffect
  };

  const handleToggleCleaningServices = (checked) => {
    setExcludeCleaningServices(checked);
  };

  const handleToggleVAT = (checked) => {
    setShowPricesWithoutVAT(checked);
  };

  const currency = (value) => `£${Number(value || 0).toFixed(2)}`;

  const getReportDateRangeKey = () => (
    dateRange?.length === 2 ? `${dateRange[0]}_to_${dateRange[1]}` : 'selected_range'
  );

  const buildItemReportParams = () => {
    const params = {};
    if (dateRange?.length === 2) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }
    if (selectedCustomerId) params.client_id = selectedCustomerId;
    if (searchText) params.search = searchText;
    if (orderStatusFilter) params.status_type = orderStatusFilter;
    if (excludeCleaningServices) params.exclude_cleaning_services = 1;
    if (showPricesWithoutVAT) params.prices_without_vat = 1;
    if (selectedItemKeys.length > 0) params.item_keys = selectedItemKeys.join(',');
    return params;
  };

  const getItemReportExportPayload = async () => {
    const response = await adminService.getItemsReport(buildItemReportParams());
    const rows = response?.data || [];
    const totals = response?.totals || {};

    if (rows.length === 0) {
      notification.warning({
        message: 'No data to export',
        description: 'There is no item report data available for the selected filters'
      });
      return null;
    }

    const headers = [
      'Product Code',
      'Product Name',
      'Quantity Sold',
      'Avg Cost Price',
      'Avg Selling Price',
      'Total Cost',
      'Total Selling',
      'Profit'
    ];

    const exportRows = rows.map((row) => ({
      'Product Code': row.product_code || 'N/A',
      'Product Name': row.product_name || 'Unknown Item',
      'Quantity Sold': itemReportQuantity(row.quantity_sold),
      'Avg Cost Price': currency(row.cost_price),
      'Avg Selling Price': currency(row.selling_price),
      'Total Cost': currency(row.total_cost_price),
      'Total Selling': currency(row.total_selling_price),
      'Profit': currency(row.profit)
    }));

    exportRows.push({
      'Product Code': '',
      'Product Name': 'TOTAL',
      'Quantity Sold': itemReportQuantity(totals.quantity_sold),
      'Avg Cost Price': '',
      'Avg Selling Price': '',
      'Total Cost': currency(totals.total_cost_price),
      'Total Selling': currency(totals.total_selling_price),
      'Profit': currency(totals.profit)
    });

    const columns = headers.map((header) => ({
      title: header,
      dataIndex: header,
      width: header === 'Product Name' ? 30 : 16
    }));

    return {
      headers,
      columns,
      exportRows,
      filename: `items_report_${getReportDateRangeKey()}`,
      title: 'Items Report',
      totals
    };
  };

// ============ FIXED EXPORT FUNCTIONS FOR SalesReport.js ============
// ============ FIXED EXPORT FUNCTIONS FOR SalesReport.js ============
  
  const exportCSV = async () => {
    if (activeTab !== 'items' && processedData.length === 0) {
      notification.warning({
        message: 'No data to export',
        description: 'There is no data available to export'
      });
      return;
    }

    try {
      if (activeTab === 'items') {
        const itemReport = await getItemReportExportPayload();
        if (!itemReport) return;

        exportUtils.exportToCSV(itemReport.exportRows, itemReport.headers, itemReport.filename);
        notification.success({
          message: 'Export Successful',
          description: 'Items report exported to CSV.'
        });
        return;
      }

      let exportData = [];
      let headers = [];
      const dateRangeStr = `${dateRange[0]}_to_${dateRange[1]}`;
      let filename = `sales_report_${dateRangeStr}`;

      // Determine which data to export based on active tab
      if (activeTab === 'overview') {
        // Export metrics data
        const overviewData = [
          { Metric: 'Total Orders', Value: metrics.totalOrders },
          { Metric: 'Successful Orders', Value: metrics.successfulOrders },
          { Metric: 'Failed Orders', Value: metrics.failedOrders },
          { Metric: 'Canceled Orders', Value: metrics.canceledOrders }
        ];
        
        // Add revenue data with proper VAT handling
        if (showPricesWithoutVAT) {
          overviewData.push(
            { Metric: 'Total Revenue (excl. VAT)', Value: `£${metrics.totalRevenue.toFixed(2)}` },
            { Metric: 'VAT Amount', Value: `£${metrics.totalVAT.toFixed(2)}` },
            { Metric: 'Total Revenue (incl. VAT)', Value: `£${(metrics.totalRevenue + metrics.totalVAT).toFixed(2)}` }
          );
        } else {
          overviewData.push(
            { Metric: 'Total Revenue', Value: `£${metrics.totalRevenue.toFixed(2)}` }
          );
        }
        
        overviewData.push(
          { Metric: 'Average Order Value', Value: `£${metrics.averageOrderValue.toFixed(2)}` }
        );
        
        exportData = overviewData;
        headers = ['Metric', 'Value'];
        filename = `sales_overview_${dateRangeStr}`;
      } 
      else if (activeTab === 'property') {
        // Export property summary data
        const propertySummaryData = preparePropertySummaryData(processedData, { 
          showPricesWithoutVAT,
          includeCanceled: true 
        });
        
        exportData = propertySummaryData.map(property => {
          const item = {
            Property: property.propertyTitle,
            Address: property.propertyAddress,
            TotalOrders: property.totalOrders,
            Successful: property.successfulOrders,
            Failed: property.failedOrders,
            Canceled: property.canceledOrders,
            Pending: property.pendingOrders
          };
          
          if (showPricesWithoutVAT) {
            item['Revenue (excl. VAT)'] = `£${property.totalRevenue.toFixed(2)}`;
            item['VAT'] = `£${property.totalVAT.toFixed(2)}`;
            item['Total (incl. VAT)'] = `£${(property.totalRevenue + property.totalVAT).toFixed(2)}`;
          } else {
            item['Revenue'] = `£${property.totalRevenue.toFixed(2)}`;
          }
          
          return item;
        });
        
        // Add TOTAL row for property summary
        const totalOrders = propertySummaryData.reduce((sum, p) => sum + p.totalOrders, 0);
        const totalSuccessful = propertySummaryData.reduce((sum, p) => sum + p.successfulOrders, 0);
        const totalFailed = propertySummaryData.reduce((sum, p) => sum + p.failedOrders, 0);
        const totalCanceled = propertySummaryData.reduce((sum, p) => sum + p.canceledOrders, 0);
        const totalPending = propertySummaryData.reduce((sum, p) => sum + p.pendingOrders, 0);
        const totalRevenue = propertySummaryData.reduce((sum, p) => sum + p.totalRevenue, 0);
        const totalVAT = propertySummaryData.reduce((sum, p) => sum + (p.totalVAT || 0), 0);
        
        const totalRow = {
          Property: 'TOTAL',
          Address: `${propertySummaryData.length} properties`,
          TotalOrders: totalOrders,
          Successful: totalSuccessful,
          Failed: totalFailed,
          Canceled: totalCanceled,
          Pending: totalPending
        };
        
        if (showPricesWithoutVAT) {
          totalRow['Revenue (excl. VAT)'] = `£${totalRevenue.toFixed(2)}`;
          totalRow['VAT'] = `£${totalVAT.toFixed(2)}`;
          totalRow['Total (incl. VAT)'] = `£${(totalRevenue + totalVAT).toFixed(2)}`;
        } else {
          totalRow['Revenue'] = `£${totalRevenue.toFixed(2)}`;
        }
        
        exportData.push(totalRow);
        
        if (showPricesWithoutVAT) {
          headers = ['Property', 'Address', 'TotalOrders', 'Successful', 'Failed', 'Canceled', 'Pending', 
            'Revenue (excl. VAT)', 'VAT', 'Total (incl. VAT)'];
        } else {
          headers = ['Property', 'Address', 'TotalOrders', 'Successful', 'Failed', 'Canceled', 'Pending', 'Revenue'];
        }
        
        filename = `property_summary_${dateRangeStr}`;
      }
      else if (activeTab === 'details') {
        // Export order details based on current view mode
        if (orderDetailsViewMode === 'items') {
          // Export orders with their detailed items - FIXED to match UI
          headers = ['Order ID', 'Date', 'Customer', 'Property', 'Address', 'Status', 'Item Name', 'Quantity'];
          
          if (showPricesWithoutVAT) {
            headers.push('Price (excl. VAT)', 'VAT Amount', 'Item Total (excl. VAT)', 'Order Total (excl. VAT)', 'Order VAT', 'Order Total (incl. VAT)');
          } else {
            headers.push('Price', 'Item Total', 'Order Total');
          }
          
          // Create flattened structure that matches the UI expandable rows
          processedData.forEach(order => {
            const orderItems = order.order_products || [];
            
            // Calculate order totals based on filters
            let orderTotal = parseFloat(order.order_total || 0);
            let orderVAT = 0;
            
            if (showPricesWithoutVAT) {
              orderVAT = parseFloat(order.vat_amount || 0);
              orderTotal = orderTotal - orderVAT; // This should be the excl. VAT amount
            }
            
            // Add main order row (header)
            const orderRow = {
              'Order ID': order.id,
              'Date': new Date(order.property_date).toLocaleDateString(),
              'Customer': `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`,
              'Property': order.order_property?.property_title || '',
              'Address': order.order_property?.apartment_address || order.delivery_address_1 || '',
              'Status': order.order_status,
              'Item Name': '--- ORDER HEADER ---',
              'Quantity': ''
            };
            
            if (showPricesWithoutVAT) {
              orderRow['Price (excl. VAT)'] = '';
              orderRow['VAT Amount'] = '';
              orderRow['Item Total (excl. VAT)'] = '';
              orderRow['Order Total (excl. VAT)'] = `£${orderTotal.toFixed(2)}`;
              orderRow['Order VAT'] = `£${orderVAT.toFixed(2)}`;
              orderRow['Order Total (incl. VAT)'] = `£${(orderTotal + orderVAT).toFixed(2)}`;
            } else {
              orderRow['Price'] = '';
              orderRow['Item Total'] = '';
              orderRow['Order Total'] = `£${orderTotal.toFixed(2)}`;
            }
            
            exportData.push(orderRow);

            // Add each item as a separate row (matching UI expandable content)
            if (orderItems.length === 0) {
              const noItemsRow = {
                'Order ID': order.id,
                'Date': '',
                'Customer': '',
                'Property': '',
                'Address': '',
                'Status': '',
                'Item Name': 'No item details available',
                'Quantity': ''
              };
              
              if (showPricesWithoutVAT) {
                noItemsRow['Price (excl. VAT)'] = '';
                noItemsRow['VAT Amount'] = '';
                noItemsRow['Item Total (excl. VAT)'] = '';
                noItemsRow['Order Total (excl. VAT)'] = '';
                noItemsRow['Order VAT'] = '';
                noItemsRow['Order Total (incl. VAT)'] = '';
              } else {
                noItemsRow['Price'] = '';
                noItemsRow['Item Total'] = '';
                noItemsRow['Order Total'] = '';
              }
              
              exportData.push(noItemsRow);
            } else {
              orderItems.forEach(item => {
                const price = parseFloat(item.order_products_price || 0);
                const qty = parseFloat(item.order_products_qty || 0);
                let itemPrice = price;
                let itemVAT = 0;
                
                if (showPricesWithoutVAT) {
                  itemVAT = parseFloat(item.vat_amount || 0);
                  itemPrice = price - itemVAT; // Assuming price includes VAT initially
                }
                
                const itemRow = {
                  'Order ID': order.id,
                  'Date': '',
                  'Customer': '',
                  'Property': '',
                  'Address': '',
                  'Status': '',
                  'Item Name': item.order_products_name || 'Unknown Item',
                  'Quantity': qty
                };
                
                if (showPricesWithoutVAT) {
                  itemRow['Price (excl. VAT)'] = `£${itemPrice.toFixed(2)}`;
                  itemRow['VAT Amount'] = `£${itemVAT.toFixed(2)}`;
                  itemRow['Item Total (excl. VAT)'] = `£${(itemPrice * qty).toFixed(2)}`;
                  itemRow['Order Total (excl. VAT)'] = '';
                  itemRow['Order VAT'] = '';
                  itemRow['Order Total (incl. VAT)'] = '';
                } else {
                  itemRow['Price'] = `£${price.toFixed(2)}`;
                  itemRow['Item Total'] = `£${(price * qty).toFixed(2)}`;
                  itemRow['Order Total'] = '';
                }
                
                exportData.push(itemRow);
              });
            }
          });
          
          filename = `orders_with_items_${dateRangeStr}`;
        }
        else if (orderDetailsViewMode === 'grouped') {
          // Export grouped by property data - FIXED to match UI
          headers = ['Property/Order', 'Order ID', 'Date', 'Customer', 'Address', 'Status'];
          
          if (showPricesWithoutVAT) {
            headers.push('Total (excl. VAT)', 'VAT Amount', 'Total (incl. VAT)');
          } else {
            headers.push('Total');
          }
          
          // Use the same grouped data structure as the UI
          const groupedData = prepareOrderDetailsData(processedData, 'grouped');
          
          groupedData.dataSource.forEach(item => {
            if (item.isPropertyGroup) {
              // Property group header
              const row = {
                'Property/Order': `${item.property} (${item.ordersCount} orders)`,
                'Order ID': '',
                'Date': '',
                'Customer': '',
                'Address': '',
                'Status': ''
              };
              
              if (showPricesWithoutVAT) {
                row['Total (excl. VAT)'] = `£${item.totalRevenue.toFixed(2)}`;
                row['VAT Amount'] = `£${item.totalVAT.toFixed(2)}`;
                row['Total (incl. VAT)'] = `£${(item.totalRevenue + item.totalVAT).toFixed(2)}`;
              } else {
                row['Total'] = `£${item.totalRevenue.toFixed(2)}`;
              }
              
              exportData.push(row);
            } else {
              // Individual order under property
              const row = {
                'Property/Order': `  Order ${item.id}`,
                'Order ID': item.id,
                'Date': new Date(item.property_date).toLocaleDateString(),
                'Customer': `${item.delivery_first_name || ''} ${item.delivery_last_name || ''}`,
                'Address': item.order_property?.apartment_address || item.delivery_address_1 || '',
                'Status': item.order_status
              };
              
              if (showPricesWithoutVAT) {
                const orderVAT = parseFloat(item.vat_amount || 0);
                const orderTotal = parseFloat(item.order_total || 0) - orderVAT;
                row['Total (excl. VAT)'] = `£${orderTotal.toFixed(2)}`;
                row['VAT Amount'] = `£${orderVAT.toFixed(2)}`;
                row['Total (incl. VAT)'] = `£${(orderTotal + orderVAT).toFixed(2)}`;
              } else {
                row['Total'] = `£${parseFloat(item.order_total || 0).toFixed(2)}`;
              }
              
              exportData.push(row);
            }
          });
          
          filename = `orders_grouped_by_property_${dateRangeStr}`;
          
          // Add GRAND TOTAL row for grouped view
          const grandTotalRevenue = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
          const grandTotalVAT = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.totalVAT || 0), 0);
          const grandTotalOrders = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.ordersCount || 0), 0);
          const totalProperties = groupedData.dataSource
            .filter(item => item.isPropertyGroup).length;
          
          const grandTotalRow = {
            'Property/Order': 'GRAND TOTAL',
            'Order ID': '',
            'Date': '',
            'Customer': `${totalProperties} properties, ${grandTotalOrders} orders`,
            'Address': '',
            'Status': ''
          };
          
          if (showPricesWithoutVAT) {
            grandTotalRow['Total (excl. VAT)'] = `£${grandTotalRevenue.toFixed(2)}`;
            grandTotalRow['VAT Amount'] = `£${grandTotalVAT.toFixed(2)}`;
            grandTotalRow['Total (incl. VAT)'] = `£${(grandTotalRevenue + grandTotalVAT).toFixed(2)}`;
          } else {
            grandTotalRow['Total'] = `£${grandTotalRevenue.toFixed(2)}`;
          }
          
          exportData.push(grandTotalRow);
        }
        else {
          // Default view - flat list of orders
          exportData = processedData.map(order => {
            const item = {
              'ID': order.id,
              'Date': new Date(order.property_date).toLocaleDateString(),
              'Customer': `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`,
              'Property': order.order_property?.property_title || '',
              'Address': order.order_property?.apartment_address || order.delivery_address_1 || '',
              'Post Code': order.order_property?.delivery_zip_code || order.delivery_zip_code || '',
              'Status': order.order_status || ''
            };
            
            if (showPricesWithoutVAT) {
              const orderVAT = parseFloat(order.vat_amount || 0);
              const orderTotal = parseFloat(order.order_total || 0) - orderVAT;
              item['Total (excl. VAT)'] = `£${orderTotal.toFixed(2)}`;
              item['VAT Amount'] = `£${orderVAT.toFixed(2)}`;
              item['Total (incl. VAT)'] = `£${(orderTotal + orderVAT).toFixed(2)}`;
            } else {
              item['Total'] = `£${parseFloat(order.order_total || 0).toFixed(2)}`;
            }
            
            item['Items'] = order.order_products ? order.order_products.length : 0;
            
            return item;
          });
          
          // Add TOTAL row for default order details
          const totalOrderValue = processedData.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
          const totalVATValue = processedData.reduce((sum, order) => sum + parseFloat(order.vat_amount || 0), 0);
          const totalItemsCount = processedData.reduce((sum, order) => sum + (order.order_products ? order.order_products.length : 0), 0);
          
          const totalRow = {
            'ID': 'TOTAL',
            'Date': '',
            'Customer': `${processedData.length} orders`,
            'Property': '',
            'Address': '',
            'Post Code': '',
            'Status': ''
          };
          
          if (showPricesWithoutVAT) {
            totalRow['Total (excl. VAT)'] = `£${(totalOrderValue - totalVATValue).toFixed(2)}`;
            totalRow['VAT Amount'] = `£${totalVATValue.toFixed(2)}`;
            totalRow['Total (incl. VAT)'] = `£${totalOrderValue.toFixed(2)}`;
          } else {
            totalRow['Total'] = `£${totalOrderValue.toFixed(2)}`;
          }
          
          totalRow['Items'] = totalItemsCount;
          
          exportData.push(totalRow);
          
          if (showPricesWithoutVAT) {
            headers = ['ID', 'Date', 'Customer', 'Property', 'Address', 'Post Code', 'Status', 
              'Total (excl. VAT)', 'VAT Amount', 'Total (incl. VAT)', 'Items'];
          } else {
            headers = ['ID', 'Date', 'Customer', 'Property', 'Address', 'Post Code', 'Status', 'Total', 'Items'];
          }
          
          filename = `orders_detail_${dateRangeStr}`;
        }
      }
      
      // Call exportToCSV from exportUtils
      exportUtils.exportToCSV(exportData, headers, filename);
      
      // Show success with totals info
      const totalRevenue = exportData.reduce((sum, item) => {
        // Extract revenue value from different possible fields
        const revenueStr = item['Revenue'] || item['Total Revenue'] || item['Total (incl. VAT)'] || item['Total'] || '£0';
        const revenue = parseFloat(revenueStr.replace('£', '').replace(/,/g, '')) || 0;
        return sum + revenue;
      }, 0);
      
      notification.success({
        message: 'Export Successful',
        description: `Sales report data exported to CSV.`
      });
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: error.message || 'Failed to export sales report data'
      });
    }
  };

  const exportExcel = async () => {
    if (activeTab !== 'items' && processedData.length === 0) {
      notification.warning({
        message: 'No data to export',
        description: 'There is no data available to export'
      });
      return;
    }

    try {
      if (activeTab === 'items') {
        const itemReport = await getItemReportExportPayload();
        if (!itemReport) return;

        exportUtils.exportToExcel(itemReport.exportRows, {
          columns: itemReport.columns,
          filename: itemReport.filename,
          sheetName: 'Items Report',
          fitToPage: true,
          orientation: 'landscape'
        });
        notification.success({
          message: 'Export Successful',
          description: 'Items report exported to Excel.'
        });
        return;
      }

      let columns = [];
      const dateRangeStr = `${dateRange[0]}_to_${dateRange[1]}`;
      let filename = `sales_report_${dateRangeStr}`;
      let sheetName = 'Sales Report';
      let exportData = [];
      let exportOptions = {};

      if (activeTab === 'property') {
        sheetName = 'Property Summary';
        filename = `property_summary_${dateRangeStr}`;

        columns = [
          { title: 'Property', dataIndex: 'propertyTitle', width: 30 },
          { title: 'Address', dataIndex: 'propertyAddress', width: 40 },
          { title: 'Total Orders', dataIndex: 'totalOrders', width: 15 },
          { title: 'Successful', dataIndex: 'successfulOrders', width: 15 },
          { title: 'Failed', dataIndex: 'failedOrders', width: 15 },
          { title: 'Canceled', dataIndex: 'canceledOrders', width: 15 },
          { title: 'Pending', dataIndex: 'pendingOrders', width: 15 }
        ];
        
        if (showPricesWithoutVAT) {
          columns.push(
            {
              title: 'Revenue (excl. VAT)',
              dataIndex: 'totalRevenue',
              width: 20,
              render: (value) => `£${value.toFixed(2)}`
            },
            {
              title: 'VAT Amount',
              dataIndex: 'totalVAT',
              width: 20,
              render: (value) => `£${value.toFixed(2)}`
            },
            {
              title: 'Total (incl. VAT)',
              key: 'totalWithVAT',
              width: 20,
              render: (_, record) => `£${(record.totalRevenue + record.totalVAT).toFixed(2)}`
            }
          );
        } else {
          columns.push({
            title: 'Total Revenue',
            dataIndex: 'totalRevenue',
            width: 20,
            render: (value) => `£${value.toFixed(2)}`
          });
        }

        exportData = preparePropertySummaryData(processedData, { 
          showPricesWithoutVAT,
          includeCanceled: true 
        });
      } 
      else if (activeTab === 'overview') {
        sheetName = 'Sales Overview';
        filename = `sales_overview_${dateRangeStr}`;

        columns = [
          { title: 'Metric', dataIndex: 'metric', width: 30 },
          { title: 'Value', dataIndex: 'value', width: 20 }
        ];

        exportData = [
          { metric: 'Total Orders', value: metrics.totalOrders },
          { metric: 'Successful Orders', value: metrics.successfulOrders },
          { metric: 'Failed Orders', value: metrics.failedOrders },
          { metric: 'Canceled Orders', value: metrics.canceledOrders }
        ];
        
        if (showPricesWithoutVAT) {
          exportData.push(
            { metric: 'Total Revenue (excl. VAT)', value: `£${metrics.totalRevenue.toFixed(2)}` },
            { metric: 'VAT Amount', value: `£${metrics.totalVAT.toFixed(2)}` },
            { metric: 'Total Revenue (incl. VAT)', value: `£${(metrics.totalRevenue + metrics.totalVAT).toFixed(2)}` }
          );
        } else {
          exportData.push(
            { metric: 'Total Revenue', value: `£${metrics.totalRevenue.toFixed(2)}` }
          );
        }
        
        exportData.push(
          { metric: 'Average Order Value', value: `£${metrics.averageOrderValue.toFixed(2)}` }
        );
      } 
      else if (activeTab === 'details') {
        sheetName = 'Order Details';
        filename = `orders_detail_${dateRangeStr}`;

        if (orderDetailsViewMode === 'items') {
          // Export orders with detailed items using nested view
          sheetName = 'Order Items Detail';
          filename = `orders_with_items_${dateRangeStr}`;
          
          columns = [
            { title: 'Order ID', dataIndex: 'id', width: 10 },
            { title: 'Date', dataIndex: 'date', width: 15 },
            { title: 'Customer', dataIndex: 'customer', width: 25 },
            { title: 'Property', dataIndex: 'property', width: 25 },
            { title: 'Address', dataIndex: 'address', width: 30 },
            { title: 'Status', dataIndex: 'status', width: 15 },
            { title: 'Item Name', dataIndex: 'itemName', width: 25 },
            { title: 'Quantity', dataIndex: 'quantity', width: 10 }
          ];
          
          if (showPricesWithoutVAT) {
            columns.push(
              { title: 'Price (excl. VAT)', dataIndex: 'itemPrice', width: 15 },
              { title: 'VAT Amount', dataIndex: 'itemVAT', width: 15 },
              { title: 'Item Total (excl. VAT)', dataIndex: 'itemTotal', width: 15 },
              { title: 'Order Total (excl. VAT)', dataIndex: 'orderTotal', width: 15 },
              { title: 'Order VAT', dataIndex: 'orderVAT', width: 15 },
              { title: 'Order Total (incl. VAT)', dataIndex: 'orderTotalIncl', width: 15 }
            );
          } else {
            columns.push(
              { title: 'Price', dataIndex: 'itemPrice', width: 15 },
              { title: 'Item Total', dataIndex: 'itemTotal', width: 15 },
              { title: 'Order Total', dataIndex: 'orderTotal', width: 15 }
            );
          }
          
          // Create nested structure for Excel
          exportData = [];
          processedData.forEach(order => {
            const orderItems = order.order_products || [];
            
            // Add order header
            const orderTotal = parseFloat(order.order_total || 0);
            const orderVAT = showPricesWithoutVAT ? parseFloat(order.vat_amount || 0) : 0;
            const orderExclVAT = showPricesWithoutVAT ? orderTotal - orderVAT : orderTotal;
            
            const orderHeader = {
              id: order.id,
              date: new Date(order.property_date).toLocaleDateString(),
              customer: `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`,
              property: order.order_property?.property_title || '',
              address: order.order_property?.apartment_address || order.delivery_address_1 || '',
              status: order.order_status,
              itemName: '--- ORDER HEADER ---',
              quantity: '',
              itemPrice: '',
              itemVAT: '',
              itemTotal: '',
              orderTotal: showPricesWithoutVAT ? `£${orderExclVAT.toFixed(2)}` : `£${orderTotal.toFixed(2)}`,
              orderVAT: showPricesWithoutVAT ? `£${orderVAT.toFixed(2)}` : '',
              orderTotalIncl: showPricesWithoutVAT ? `£${orderTotal.toFixed(2)}` : '',
              isOrderHeader: true
            };
            
            exportData.push(orderHeader);
            
            // Add items
            if (orderItems.length === 0) {
              exportData.push({
                id: order.id,
                itemName: 'No item details available',
                isOrderHeader: false
              });
            } else {
              orderItems.forEach(item => {
                const price = parseFloat(item.order_products_price || 0);
                const qty = parseFloat(item.order_products_qty || 0);
                const itemVAT = showPricesWithoutVAT ? parseFloat(item.vat_amount || 0) : 0;
                const itemExclVAT = showPricesWithoutVAT ? price - itemVAT : price;
                
                exportData.push({
                  id: order.id,
                  itemName: item.order_products_name || 'Unknown Item',
                  quantity: qty,
                  itemPrice: showPricesWithoutVAT ? `£${itemExclVAT.toFixed(2)}` : `£${price.toFixed(2)}`,
                  itemVAT: showPricesWithoutVAT ? `£${itemVAT.toFixed(2)}` : '',
                  itemTotal: showPricesWithoutVAT ? `£${(itemExclVAT * qty).toFixed(2)}` : `£${(price * qty).toFixed(2)}`,
                  isOrderHeader: false
                });
              });
            }
          });
          
          // Add GRAND TOTAL row
          const totalOrderValue = processedData.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
          const totalVATValue = processedData.reduce((sum, order) => sum + parseFloat(order.vat_amount || 0), 0);
          const totalItemsCount = processedData.reduce((sum, order) => sum + (order.order_products ? order.order_products.length : 0), 0);
          
          // Calculate total item values
          let totalItemValue = 0;
          let totalItemVATValue = 0;
          processedData.forEach(order => {
            const orderItems = order.order_products || [];
            orderItems.forEach(item => {
              const price = parseFloat(item.order_products_price || 0);
              const qty = parseFloat(item.order_products_qty || 0);
              const itemVAT = showPricesWithoutVAT ? parseFloat(item.vat_amount || 0) : 0;
              totalItemValue += (price - itemVAT) * qty;
              totalItemVATValue += itemVAT * qty;
            });
          });
          
          const grandTotalRow = {
            id: 'GRAND TOTAL',
            date: '',
            customer: `${processedData.length} orders`,
            property: `${totalItemsCount} items`,
            address: '',
            status: '',
            itemName: 'ALL ITEMS TOTAL',
            quantity: totalItemsCount,
            itemPrice: '',
            itemVAT: showPricesWithoutVAT ? `£${totalItemVATValue.toFixed(2)}` : '',
            itemTotal: showPricesWithoutVAT ? `£${totalItemValue.toFixed(2)}` : `£${totalItemValue.toFixed(2)}`,
            orderTotal: showPricesWithoutVAT ? `£${(totalOrderValue - totalVATValue).toFixed(2)}` : `£${totalOrderValue.toFixed(2)}`,
            orderVAT: showPricesWithoutVAT ? `£${totalVATValue.toFixed(2)}` : '',
            orderTotalIncl: showPricesWithoutVAT ? `£${totalOrderValue.toFixed(2)}` : '',
            isOrderHeader: false,
            isGrandTotal: true
          };
          
          exportData.push(grandTotalRow);
          
          exportOptions.nestedItems = true;
        }
        else if (orderDetailsViewMode === 'grouped') {
          // Export grouped by property
          sheetName = 'Orders Grouped by Property';
          filename = `orders_grouped_by_property_${dateRangeStr}`;
          
          columns = [
            { title: 'Property/Order', dataIndex: 'property', width: 30 },
            { title: 'Order ID', dataIndex: 'id', width: 10 },
            { title: 'Date', dataIndex: 'date', width: 15 },
            { title: 'Customer', dataIndex: 'customer', width: 25 },
            { title: 'Address', dataIndex: 'address', width: 30 },
            { title: 'Status', dataIndex: 'status', width: 15 }
          ];
          
          if (showPricesWithoutVAT) {
            columns.push(
              { title: 'Total (excl. VAT)', dataIndex: 'totalExclVAT', width: 15 },
              { title: 'VAT Amount', dataIndex: 'vatAmount', width: 15 },
              { title: 'Total (incl. VAT)', dataIndex: 'totalInclVAT', width: 15 }
            );
          } else {
            columns.push({ title: 'Total', dataIndex: 'total', width: 15 });
          }
          
          // Get grouped data from UI
          const groupedData = prepareOrderDetailsData(processedData, 'grouped');
          exportData = groupedData.dataSource;
          
          // Add GRAND TOTAL row
          const grandTotalRevenue = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
          const grandTotalVAT = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.totalVAT || 0), 0);
          const grandTotalOrders = groupedData.dataSource
            .filter(item => item.isPropertyGroup)
            .reduce((sum, item) => sum + (item.ordersCount || 0), 0);
          const totalProperties = groupedData.dataSource
            .filter(item => item.isPropertyGroup).length;
          
          const grandTotalRow = {
            property: 'GRAND TOTAL',
            id: '',
            date: '',
            customer: `${totalProperties} properties, ${grandTotalOrders} orders`,
            address: '',
            status: '',
            totalExclVAT: showPricesWithoutVAT ? `£${grandTotalRevenue.toFixed(2)}` : `£${grandTotalRevenue.toFixed(2)}`,
            vatAmount: showPricesWithoutVAT ? `£${grandTotalVAT.toFixed(2)}` : '',
            totalInclVAT: showPricesWithoutVAT ? `£${(grandTotalRevenue + grandTotalVAT).toFixed(2)}` : '',
            total: !showPricesWithoutVAT ? `£${grandTotalRevenue.toFixed(2)}` : '',
            isPropertyGroup: false,
            isGrandTotal: true
          };
          
          exportData.push(grandTotalRow);
          exportOptions.groupedView = true;
        }
        else {
          // Default flat view
          columns = [
            { title: 'Order ID', dataIndex: 'id', width: 10 },
            { title: 'Date', dataIndex: 'property_date', width: 15, render: (date) => new Date(date).toLocaleDateString() },
            { title: 'Customer', key: 'customer', width: 25, render: (_, record) => `${record.delivery_first_name || ''} ${record.delivery_last_name || ''}` },
            { title: 'Property', key: 'property', width: 25, render: (_, record) => record.order_property?.property_title || '' },
            { title: 'Address', key: 'address', width: 30, render: (_, record) => record.order_property?.apartment_address || record.delivery_address_1 || '' },
            { title: 'Post Code', key: 'post_code', width: 15, render: (_, record) => record.order_property?.delivery_zip_code || record.delivery_zip_code || '' },
            { title: 'Status', dataIndex: 'order_status', width: 15 }
          ];
          
          if (showPricesWithoutVAT) {
            columns.push(
              { title: 'Total (excl. VAT)', dataIndex: 'order_total', width: 15, render: (total, record) => `£${(parseFloat(total || 0) - parseFloat(record.vat_amount || 0)).toFixed(2)}` },
              { title: 'VAT Amount', key: 'vat_amount', width: 15, render: (_, record) => `£${parseFloat(record.vat_amount || 0).toFixed(2)}` },
              { title: 'Total (incl. VAT)', dataIndex: 'order_total', width: 15, render: (total) => `£${parseFloat(total || 0).toFixed(2)}` }
            );
          } else {
            columns.push({ title: 'Total', dataIndex: 'order_total', width: 15, render: (total) => `£${parseFloat(total || 0).toFixed(2)}` });
          }
          
          columns.push({ title: 'Items Count', key: 'items_count', width: 15, render: (_, record) => record.order_products ? record.order_products.length : 0 });
          
          exportData = processedData;
        }
      }

      exportUtils.exportToExcel(exportData, {
        columns,
        filename,
        sheetName,
        fitToPage: true,
        orientation: 'landscape',
        ...exportOptions
      });

      // Calculate total for success message
      let totalRevenue = 0;
      if (activeTab === 'property') {
        totalRevenue = exportData.reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
      } else if (activeTab === 'details') {
        totalRevenue = processedData.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
      } else if (activeTab === 'overview') {
        totalRevenue = metrics.totalRevenue || 0;
      }

      notification.success({
        message: 'Export Successful',
        description: `Sales report data exported to Excel.`
      });
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: 'Failed to export sales report data: ' + error.message
      });
    }
  };


const exportPDF = async () => {
  if (activeTab !== 'items' && processedData.length === 0) {
    notification.warning({
      message: 'No data to export',
      description: 'There is no data available to export'
    });
    return;
  }

  try {
    if (activeTab === 'items') {
      const itemReport = await getItemReportExportPayload();
      if (!itemReport) return false;

      const exported = exportUtils.exportToPDF(itemReport.exportRows, {
        columns: itemReport.columns,
        filename: itemReport.filename,
        title: 'Items Report',
        dateRange: dateRange?.length === 2 ? `${dateRange[0]} to ${dateRange[1]}` : '',
        orientation: 'landscape',
        fontSize: 8,
        summary: {
          'Quantity Sold': itemReportQuantity(itemReport.totals.quantity_sold),
          'Total Cost': currency(itemReport.totals.total_cost_price),
          'Total Selling': currency(itemReport.totals.total_selling_price),
          'Profit': currency(itemReport.totals.profit)
        }
      });

      if (!exported) return false;

      notification.success({
        message: 'PDF Export Successful',
        description: 'Items report exported successfully.',
        duration: 4,
      });
      return true;
    }

    const dateRangeStr = `${dateRange[0]}_to_${dateRange[1]}`;
    let filename = `sales_report_${dateRangeStr}`;
    
    // Determine filename based on active tab and client
    const selectedClient = selectedCustomerId ? 
      customers.find(c => c.id === selectedCustomerId) : null;
    
    if (selectedClient) {
      const clientName = (selectedClient.name || 
                        `${selectedClient.first_name || ''}_${selectedClient.last_name || ''}`).replace(/\s+/g, '_');
      filename = `${activeTab}_${clientName}_${filename}`;
    } else {
      filename = `${activeTab}_all_clients_${filename}`;
    }

    // Show loading notification
    const loadingNotification = notification.info({
      key: 'pdf-loading',
      message: 'Generating PDF...',
      description: 'Please wait while your PDF is being generated.',
      duration: 0, // Don't auto close
    });

    // Generate PDF using react-pdf
    const pdfDocument = (
      <SalesReportPDF
    reportData={processedData}
    activeTab={activeTab}
    metrics={metrics}
    dateRange={dateRange}
    selectedClient={selectedClient}
    showPricesWithoutVAT={showPricesWithoutVAT}
    orderDetailsViewMode={orderDetailsViewMode}
    preparePropertySummaryData={preparePropertySummaryData}
    prepareOrderDetailsData={prepareOrderDetailsData}
  
      />
    );

    // Create PDF blob
    const blob = await pdf(pdfDocument).toBlob();
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Close loading notification
    notification.destroy('pdf-loading');

    // Calculate total revenue for success message
    let totalRevenue = 0;
    if (activeTab === 'property') {
      const propertySummaryData = preparePropertySummaryData(processedData, { 
        showPricesWithoutVAT,
        includeCanceled: true 
      });
      totalRevenue = propertySummaryData.reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
      if (showPricesWithoutVAT) {
        const totalVAT = propertySummaryData.reduce((sum, item) => sum + (item.totalVAT || 0), 0);
        totalRevenue += totalVAT;
      }
    } else if (activeTab === 'details') {
      totalRevenue = processedData.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
    } else if (activeTab === 'overview') {
      totalRevenue = metrics.totalRevenue || 0;
      if (showPricesWithoutVAT) {
        totalRevenue += metrics.totalVAT || 0;
      }
    }

    // Success notification
    notification.success({
      message: 'PDF Export Successful',
      description: `Sales report exported successfully. Total: £${totalRevenue.toFixed(2)} | ${selectedClient ? `Client: ${selectedClient.name || selectedClient.first_name + ' ' + selectedClient.last_name}` : 'All Clients'}`,
      duration: 4,
    });

    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    notification.error({
      message: 'PDF Export Failed',
      description: 'Failed to generate PDF: ' + error.message,
      duration: 4,
    });
    return false;
  }
  };

  const printReport = () => {
    if (activeTab !== 'items' && processedData.length === 0) {
      notification.warning({
        message: 'No data to print',
        description: 'There is no data available to print'
      });
      return;
    }

    try {
      // Get the report element for printing
      const reportElement = document.getElementById('sales-report-content');

      if (!reportElement) {
        throw new Error('Report content not found');
      }

      const dateRangeStr = `${dateRange[0]} to ${dateRange[1]}`;
      let title = 'Sales Report';

      if (activeTab === 'overview') {
        title = 'Sales Overview Report';
      } else if (activeTab === 'property') {
        title = 'Property Summary Report';
      } else if (activeTab === 'items') {
        title = 'Items Report';
      } else {
        title = 'Order Details Report';
      }

      // Print the element
      exportUtils.printElement(reportElement, `${title} (${dateRangeStr})`);

      notification.success({
        message: 'Print Initiated',
        description: 'The print dialog has been opened'
      });
    } catch (error) {
      notification.error({
        message: 'Print Failed',
        description: error.message || 'Failed to print sales report'
      });
    }
  };

  // Export menu dropdown
  const exportMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={exportCSV} icon={<FileTextOutlined />}>
        Export as CSV
      </Menu.Item>
      <Menu.Item key="excel" onClick={exportExcel} icon={<FileExcelOutlined />}>
        Export as Excel
      </Menu.Item>
      <Menu.Item key="pdf" onClick={exportPDF} icon={<FilePdfOutlined />}>
        Export as PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={printReport} icon={<PrinterOutlined />}>
        Print Report
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-golden-navy flex items-center">
          <FileTextOutlined className="mr-2" />
          Sales Report
        </h1>
        <Dropdown
          overlay={exportMenu}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            className="bg-golden-navy"
          >
            Export <DownloadOutlined />
          </Button>
        </Dropdown>
      </div>

      <ReportFilters
        dateViewMode={dateViewMode}
        currentDate={currentDate}
        dateRange={dateRange}
        excludeCleaningServices={excludeCleaningServices}
        showPricesWithoutVAT={showPricesWithoutVAT}
        dateDisplayText={dateDisplayText}
        isCustomRange={isCustomRange}
        selectedCustomerId={selectedCustomerId}
        searchText={searchText}
        orderStatusFilter={orderStatusFilter}
        customers={customers}
        handleDateNavigation={handleDateNavigation}
        handleDateViewModeChange={handleDateViewModeChange}
        handleCustomRangeChange={handleCustomRangeChange}
        handleCustomerChange={handleCustomerChange}
        handleStatusFilterChange={handleStatusFilterChange}
        handleSearch={handleSearch}
        handleResetFilters={handleResetFilters}
        handleToggleCleaningServices={handleToggleCleaningServices}
        handleToggleVAT={handleToggleVAT}
        fetchReportData={fetchReportData}
        onDataLoaded={handleDataLoaded}
        isClient={isClient}
      />

      <MetricsCards metrics={metrics} showPricesWithoutVAT={showPricesWithoutVAT} />

      {/* Tabs for different views with content ID for printing */}
      <div id="sales-report-content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="bg-white shadow-md rounded-lg p-4 mb-6"
          tabBarExtraContent={
            <Dropdown
              overlay={exportMenu}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
                className="bg-golden-navy mr-2"
              >
                Export
              </Button>
            </Dropdown>
          }
        >
          <TabPane
            tab={<span><AppstoreOutlined />Overview</span>}
            key="overview"
          >
            <OverviewTab 
              reportData={processedData} 
              excludeCleaningServices={excludeCleaningServices}
              showPricesWithoutVAT={showPricesWithoutVAT}
            />
          </TabPane>

          <TabPane
            tab={<span><UserOutlined />Property Summary</span>}
            key="property"
          >
            <PropertySummaryTab
              reportData={processedData}
              loading={loading}
              excludeCleaningServices={excludeCleaningServices}
              showPricesWithoutVAT={showPricesWithoutVAT}
            />
          </TabPane>

          <TabPane
            tab={<span><ShoppingOutlined />Order Details</span>}
            key="details"
          >
            <OrderDetailsTab
              reportData={processedData}
              loading={loading}
              excludeCleaningServices={excludeCleaningServices}
              showPricesWithoutVAT={showPricesWithoutVAT}
              exportMenu={exportMenu}
              onViewModeChange={handleOrderDetailsViewMode}
              isClient={isClient}
            />
          </TabPane>

          {canViewAdminOnlyReports && (
            <TabPane
              tab={<span><TableOutlined />Items Report</span>}
              key="items"
            >
              <ItemReportTab
                dateRange={dateRange}
                selectedCustomerId={selectedCustomerId}
                searchText={searchText}
                orderStatusFilter={orderStatusFilter}
                excludeCleaningServices={excludeCleaningServices}
                showPricesWithoutVAT={showPricesWithoutVAT}
                selectedItemKeys={selectedItemKeys}
                onSelectedItemKeysChange={setSelectedItemKeys}
              />
            </TabPane>
          )}

          <TabPane
            tab={<span><ClockCircleOutlined />Hire Duration</span>}
            key="hire-duration"
          >
            <HireDurationReportTab
              dateRange={dateRange}
              selectedCustomerId={selectedCustomerId}
              isClient={isClient}
            />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default SalesReport;
